import { useEffect } from "react";
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { z } from "zod";
import { IltCoursePage } from "@/features/instructor-led/components/IltCoursePage";
import { IltLoadError, IltNotFound } from "@/features/instructor-led/components/IltNotFound";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";
import { getInstructorLedGetCourseQueryOptions } from "@/services/api/instructor-led/instructor-led";
import { queryClient } from "@/services/queryClient";

// `session` opens the page at one of its sessions, e.g. coming back from sign-in to book it. Stripe
// Checkout returns here after paying for a seat on it (LMS-37) with `checkout` (success or
// cancelled) and, on success, its own `session_id`, or when cancelled our `checkout_id`: they drive a
// confirmation (or freeing the held seat), not the booking itself, which the webhook makes.
// `purchase=seats` marks a checkout for several seats on the session to hand out (LMS-190).
const iltCourseSearchSchema = z.object({
  session: z.string().optional(),
  checkout: z.enum(["success", "cancelled"]).optional(),
  purchase: z.enum(["seats"]).optional(),
  session_id: z.string().optional(),
  checkout_id: z.string().optional(),
});

export const Route = createFileRoute("/_marketing/face-to-face-training_/$courseId")({
  validateSearch: iltCourseSearchSchema,
  // Face-to-face training is hidden while the ilt flag is off (the API 404s it too).
  beforeLoad: async () => {
    const { ilt } = await fetchFeatureFlags();
    if (!ilt) throw notFound();
  },
  loader: async ({ params: { courseId } }) => {
    try {
      // Always asked afresh: spaces left change, and the API answers 404 once the course or the ilt
      // flag is switched off, which a cached course would hide.
      return await queryClient.fetchQuery({
        ...getInstructorLedGetCourseQueryOptions(courseId),
        staleTime: 0,
      });
    } catch (error) {
      // An online, draft or retired course answers 404 just like one that doesn't exist.
      if (isAxiosError(error) && error.response?.status === 404) throw notFound();
      throw error;
    }
  },
  notFoundComponent: IltNotFound,
  errorComponent: IltLoadError,
  component: IltCourseRoutePage,
});

function IltCourseRoutePage() {
  const router = useRouter();
  const course = Route.useLoaderData();
  const { session, checkout, checkout_id: checkoutId, purchase } = Route.useSearch();

  // Opened without a session: start at the top, as the online course page does.
  useEffect(() => {
    if (!session) window.scrollTo({ top: 0, behavior: "auto" });
  }, [course.id, session]);

  return (
    <IltCoursePage
      course={course}
      selectedSessionId={session}
      checkout={checkout}
      checkoutId={checkoutId}
      purchase={purchase}
      // Reload the course so spaces left and Booked show the booking just made (or refused).
      onBookingChanged={() => void router.invalidate()}
    />
  );
}
